/**
 * Workspace branding: the report letterhead (CLAUDE.md 8.1, 12.1).
 *
 * Present in **every** workspace, personal ones included. That is the part it would be
 * easy to get wrong: 12.1 says in as many words that a sole practitioner has a
 * letterhead too, and hiding this from them would mean their reports go out carrying
 * nothing but the software's name.
 *
 * Two rules this panel exists to honour:
 *
 * - **The limits are shown before a file is refused.** A logo rejected after the
 *   upload, by an error toast, teaches nothing; the accepted formats and sizes are on
 *   screen next to the picker, and a file that breaks them is refused here, locally,
 *   before the bytes leave the machine. The server checks the same rules again --
 *   a client-side check is a courtesy, not a control (15.3).
 * - **The preview is fetched, not linked.** The logo endpoint sits behind the bearer
 *   token, so an `<img src>` pointing at the API would arrive unauthenticated. The
 *   bytes go through `useBlobUrl` like every other protected file in the product.
 */
import { useEffect, useId, useRef, useState, type DragEvent } from 'react'
import { ImageIcon, Trash2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import {
  useDeleteWorkspaceLogo,
  useUpdateWorkspace,
  useUploadWorkspaceLogo,
  useWorkspace,
  workspaceLogoPath,
} from '@/api/workspaces'
import type { Letterhead } from '@/api/types'
import { useBlobUrl } from '@/components/support/useBlobUrl'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/cn'
import { formatBytes, formatNumber } from '@/lib/format'

/**
 * The server's own limits, restated so a file can be refused before it is sent.
 * They are `app/services/branding.py`'s constants; if they move there, they move here.
 */
const MAX_LOGO_BYTES = 2 * 1024 * 1024
const MAX_LOGO_DIMENSION = 4000
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

const EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
}

/** The browser's idea of the type, falling back to the extension for a dropped file. */
function guessType(file: File): string {
  if (file.type) return file.type
  return EXTENSION_TYPES[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? ''
}

export function LetterheadPanel() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { data: workspace } = useWorkspace()
  const workspaceId = workspace?.id ?? null

  const upload = useUploadWorkspaceLogo(workspaceId)
  const removeLogo = useDeleteWorkspaceLogo(workspaceId)
  const save = useUpdateWorkspace(workspaceId)

  const inputId = useId()
  const [dragging, setDragging] = useState(false)
  const [rejection, setRejection] = useState<string | null>(null)

  // Untouched fields stay undefined, so a save sends only what changed.
  const [draft, setDraft] = useState<Partial<Letterhead>>({})
  // A workspace switch must not carry one firm's letterhead into another's form.
  const editing = useRef(workspaceId)
  useEffect(() => {
    if (editing.current !== workspaceId) {
      editing.current = workspaceId
      setDraft({})
      setRejection(null)
    }
  }, [workspaceId])

  const logo = useBlobUrl(
    workspace?.has_logo && workspaceId ? workspaceLogoPath(workspaceId) : null,
  )

  if (!workspace) return null

  // The server is the authority; this only decides whether to render the controls.
  const canEdit = workspace.capabilities.includes('workspace:manage_settings')
  const busy = upload.isPending || removeLogo.isPending
  const field = (key: keyof Letterhead): string => draft[key] ?? workspace.letterhead[key] ?? ''
  const dirty = Object.keys(draft).length > 0

  const failed = (error: unknown) =>
    toast({
      title: t('common.error'),
      description: error instanceof ApiError ? error.localised : undefined,
      variant: 'error',
    })

  const accept = (files: FileList | File[] | null) => {
    const file = Array.from(files ?? [])[0]
    if (!file || !canEdit) return
    if (!ALLOWED_TYPES.includes(guessType(file))) {
      setRejection(t('workspace.logo_rejected_type', { name: file.name }))
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setRejection(
        t('workspace.logo_rejected_size', { name: file.name, max: formatBytes(MAX_LOGO_BYTES) }),
      )
      return
    }
    setRejection(null)
    upload.mutate(file, {
      onSuccess: () => toast({ title: t('common.saved'), variant: 'success' }),
      onError: failed,
    })
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    accept(event.dataTransfer.files)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.letterhead')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-text-muted">{t('workspace.letterhead_hint')}</p>

        <div className="space-y-2">
          <p className="text-sm font-medium">{t('workspace.logo')}</p>
          <div
            className={cn(
              'flex flex-wrap items-center gap-4 rounded-md border border-dashed p-3 transition-colors',
              dragging ? 'border-accent bg-accent-subtle' : 'border-border',
            )}
            onDragOver={(event) => {
              event.preventDefault()
              if (canEdit) setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <span className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-surface-raised">
              {workspace.has_logo && logo.status === 'ready' ? (
                <img
                  src={logo.url}
                  alt={t('workspace.logo_preview')}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <ImageIcon size={22} aria-hidden className="text-text-muted" />
              )}
            </span>

            <div className="min-w-0 flex-1 space-y-2">
              <input
                id={inputId}
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                className="sr-only"
                disabled={!canEdit || busy}
                onChange={(event) => {
                  accept(event.target.files)
                  event.target.value = ''
                }}
              />
              <div className="flex flex-wrap items-center gap-2">
                <label
                  htmlFor={inputId}
                  className={cn(
                    'inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm text-text hover:bg-surface-raised',
                    (!canEdit || busy) && 'pointer-events-none opacity-50',
                  )}
                >
                  <Upload size={14} aria-hidden />
                  {workspace.has_logo ? t('workspace.logo_replace') : t('workspace.logo_add')}
                </label>
                {workspace.has_logo && (
                  <Button
                    disabled={!canEdit}
                    loading={removeLogo.isPending}
                    onClick={() =>
                      removeLogo.mutate(undefined, {
                        onSuccess: () => {
                          setRejection(null)
                          toast({ title: t('common.saved'), variant: 'success' })
                        },
                        onError: failed,
                      })
                    }
                  >
                    <Trash2 size={14} aria-hidden />
                    {t('workspace.logo_remove')}
                  </Button>
                )}
              </div>
              {/* Stated up front, not after a refusal: a limit discovered by hitting
                  it is a limit that wasted somebody's upload. */}
              <p className="text-xs text-text-muted">
                {t('workspace.logo_limits', {
                  max: formatBytes(MAX_LOGO_BYTES),
                  pixels: formatNumber(MAX_LOGO_DIMENSION, { decimals: 0, thousands: false }),
                })}
              </p>
              {!workspace.has_logo && (
                <p className="text-xs text-text-muted">
                  {canEdit ? t('workspace.logo_drop') : t('workspace.logo_none')}
                </p>
              )}
              {rejection && (
                <p role="alert" className="text-xs text-[var(--status-fail)]">
                  {rejection}
                </p>
              )}
            </div>
          </div>
        </div>

        <Field description={t('workspace.letterhead_header_hint')}>
          <FieldLabel>{t('workspace.letterhead_header')}</FieldLabel>
          <FieldInput
            value={field('header_text')}
            maxLength={200}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, header_text: event.target.value })}
          />
        </Field>

        <Field>
          <FieldLabel>{t('workspace.letterhead_address')}</FieldLabel>
          {/* The only multi-line field; the report keeps its line breaks. */}
          <textarea
            rows={3}
            maxLength={500}
            disabled={!canEdit}
            value={field('address')}
            aria-label={t('workspace.letterhead_address')}
            onChange={(event) => setDraft({ ...draft, address: event.target.value })}
            className="w-full resize-y rounded-md border border-border bg-surface p-3 text-sm leading-relaxed text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] disabled:opacity-50"
          />
        </Field>

        <Field>
          <FieldLabel>{t('workspace.letterhead_contact')}</FieldLabel>
          <FieldInput
            value={field('contact')}
            maxLength={200}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, contact: event.target.value })}
          />
        </Field>

        <Field description={t('workspace.letterhead_footer_hint')}>
          <FieldLabel>{t('workspace.letterhead_footer')}</FieldLabel>
          <FieldInput
            value={field('footer_text')}
            maxLength={200}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, footer_text: event.target.value })}
          />
        </Field>

        {canEdit && (
          <Button
            variant="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { letterhead: { ...workspace.letterhead, ...draft } },
                {
                  onSuccess: () => {
                    setDraft({})
                    toast({ title: t('common.saved'), variant: 'success' })
                  },
                  onError: failed,
                },
              )
            }
          >
            {t('action.save')}
          </Button>
        )}
      </CardBody>
    </Card>
  )
}
